import { LandingSectionEyebrow } from '@/components/landing/landing-section-eyebrow'
import { LANDING_FAQ_CATEGORIES, LANDING_SECTION_GRADIENT } from '@/lib/landing-content'
import { ChevronDown } from 'lucide-react'
import { useState } from 'react'

export function LandingFaqSection() {
  const [openFaqItems, setOpenFaqItems] = useState<Set<string>>(new Set())

  const toggleFaqItem = (id: string) => {
    setOpenFaqItems((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <section
      id="faq"
      className="py-12 md:py-32 scroll-fade-up"
      style={{ backgroundImage: LANDING_SECTION_GRADIENT }}
    >
      <div className="container mx-auto px-4 md:px-16">
        <div className="max-w-[1280px] mx-auto">
          <div className="mb-10 md:mb-16">
            <LandingSectionEyebrow label="FREQUENTLY ASKED QUESTIONS" className="mb-4" />
            <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-start">
              <div>
                <div className="flex flex-col gap-1 mb-6">
                  <h2 className="text-3xl md:text-[48px] font-display text-gray-900 leading-[1.2] tracking-[-0.96px]">
                    Questions?
                  </h2>
                  <p className="text-3xl md:text-[48px] font-display text-gray-400 leading-[1.2] tracking-[-0.96px]">
                    We have answers.
                  </p>
                </div>
                <p className="text-base font-medium text-gray-500 tracking-[-0.32px] mb-6 md:mb-8">
                  We guide employees through one of the toughest moments in their careers and give
                  them a real chance at a fresh start. Our tools combine structured support with
                  personalized coaching and job-ready training.
                </p>
              </div>

              <div className="flex flex-col gap-6 md:gap-8">
                {LANDING_FAQ_CATEGORIES.map((category) => (
                  <div key={category.title} className="flex flex-col gap-4">
                    <h3 className="text-xl font-medium text-gray-900 tracking-[-0.4px]">
                      {category.title}
                    </h3>
                    {category.items.map((item) => (
                      <div
                        key={item.id}
                        className="bg-white rounded-lg overflow-hidden"
                        style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}
                      >
                        <button
                          type="button"
                          onClick={() => toggleFaqItem(item.id)}
                          aria-expanded={openFaqItems.has(item.id)}
                          aria-controls={`${item.id}-answer`}
                          className="w-full flex items-center justify-between p-4 md:p-6 text-left hover:bg-gray-50 transition-colors"
                        >
                          <h4 className="text-base font-medium text-gray-900 tracking-[-0.32px] pr-4">
                            {item.question}
                          </h4>
                          <ChevronDown
                            className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform ${
                              openFaqItems.has(item.id) ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        <div
                          id={`${item.id}-answer`}
                          className={`grid transition-[grid-template-rows] duration-200 ${
                            openFaqItems.has(item.id) ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                          }`}
                        >
                          <div className="overflow-hidden">
                            <div className="px-4 pb-4 md:px-6 md:pb-6">
                              <p className="text-sm font-medium text-gray-500 tracking-[-0.28px] leading-relaxed">
                                {item.answer}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
